import type { TFunction } from 'i18next';
import {
  DOCUMENT_LIKE_ACTIVITY_EVENT_FAMILIES,
  isProductionDeclarationActivityEventCode,
  KEY_DATE_ACTIVITY_EVENT_CODES,
  STATUS_EVENT_FAMILY,
  SYNC_ACTIVITY_EVENT_CODE,
  UPDATE_ACTIVITY_EVENT_CODE,
} from '../config/purchase-order-activity-config';
import { formatActivityEventCode } from '../helpers/purchase-order-activity-text';
import type {
  PurchaseOrderActivityDetailState,
  PurchaseOrderActivityItem,
} from '../types/purchase-order-activity-item';
import { PurchaseOrderActivityKeyDates } from './purchase-order-activity-key-dates';
import { PurchaseOrderActivityLineSynced } from './purchase-order-activity-line-synced';
import { PurchaseOrderActivityLineUpdated } from './purchase-order-activity-line-updated';
import { PurchaseOrderActivityMailSent } from './purchase-order-activity-mail-sent';
import { renderDocumentActivity } from './purchase-order-activity-render-documents';
import { PurchaseOrderActivityStatusLabel } from './purchase-order-activity-status-label';

type PurchaseOrderActivityRenderProps = {
  activity: PurchaseOrderActivityItem;
  locale: string;
  onOpenDetail: (detail: PurchaseOrderActivityDetailState) => void;
  documentUrlByName: Record<string, string>;
  t: TFunction<'purchaseOrder'>;
};

export function renderActivityContent({
  activity,
  locale,
  onOpenDetail,
  documentUrlByName,
  t,
}: PurchaseOrderActivityRenderProps) {
  if (activity.eventFamily === STATUS_EVENT_FAMILY) {
    return (
      <>
        <span className='font-semibold'>{t('activity.statusLabel')}:</span>
        {activity.oldValue ? (
          <>
            <PurchaseOrderActivityStatusLabel t={t} value={activity.oldValue} />
            <span className='text-muted-foreground'>→</span>
          </>
        ) : null}
        <PurchaseOrderActivityStatusLabel t={t} value={activity.newValue} />
      </>
    );
  }

  if (isProductionDeclarationActivityEventCode(activity.eventCode)) {
    return <PurchaseOrderActivityMailSent activity={activity} t={t} />;
  }

  if (
    DOCUMENT_LIKE_ACTIVITY_EVENT_FAMILIES.includes(
      activity.eventFamily as (typeof DOCUMENT_LIKE_ACTIVITY_EVENT_FAMILIES)[number],
    )
  ) {
    return renderDocumentActivity({ activity, documentUrlByName, t });
  }

  if (activity.eventCode === SYNC_ACTIVITY_EVENT_CODE) {
    return (
      <PurchaseOrderActivityLineSynced
        activity={activity}
        onOpenDetail={onOpenDetail}
        t={t}
      />
    );
  }

  if (activity.eventCode === UPDATE_ACTIVITY_EVENT_CODE) {
    return (
      <PurchaseOrderActivityLineUpdated
        activity={activity}
        onOpenDetail={onOpenDetail}
        t={t}
      />
    );
  }

  if (KEY_DATE_ACTIVITY_EVENT_CODES.includes(activity.eventCode)) {
    return (
      <PurchaseOrderActivityKeyDates
        activity={activity}
        locale={locale}
        t={t}
      />
    );
  }

  return (
    <span className='font-semibold'>
      {formatActivityEventCode(activity.eventCode)}
    </span>
  );
}
